import React, { useEffect, useMemo, useState } from 'react'
import { HiUsers } from 'react-icons/hi'
import { toast } from 'react-toastify'
import { appRef } from '../../app'
import { HOST_URL } from '../../config'
import { HttpStatus } from '../../constants/http-status.enum'
import type { ApiResponse } from '../../api'
import type { IUser } from '../../api/auth'
import {
    deleteUserMobileDevice,
    getPermissionCatalog,
    getUserOverrides,
    getUserRoles,
    listRoles,
    listUserMobileDevices,
    replaceUserOverrides,
    replaceUserRoles,
    setUserMobileDeviceApproval,
    type IPermission,
    type IRole,
    type IUserMobileDevice,
    type IUserPermissionOverride,
} from '../../api/security'
import { getLocale } from '../../locale'
import {
    PERMISSION_GROUP_LABELS,
    PERMISSION_GROUP_ORDER,
    getPermissionLabel,
} from '../roles-permissions/permission-labels'

type UsersState = IUser[]

async function fetchUsers(): Promise<IUser[]> {
    const response = await fetch(`${HOST_URL}users`, { credentials: 'include' })
    const result = (await response.json()) as ApiResponse<IUser[]>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result.data
}

async function createUser(username: string, password: string): Promise<void> {
    const response = await fetch(`${HOST_URL}users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ username, password }),
    })
    const result = (await response.json()) as ApiResponse<IUser>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
}

async function patchUser(userId: number, patch: Partial<IUser>): Promise<void> {
    const response = await fetch(`${HOST_URL}users/${userId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(patch),
    })
    const result = (await response.json()) as ApiResponse<IUser>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
}

async function removeUser(userId: number): Promise<void> {
    const response = await fetch(`${HOST_URL}users/${userId}`, {
        method: 'DELETE',
        credentials: 'include',
    })
    const result = (await response.json()) as ApiResponse<IUser>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
}

export const UsersManagementPage: React.FC = () => {
    const isHebrew = getLocale() === 'he'
    const [users, setUsers] = useState<UsersState>([])
    const [roles, setRoles] = useState<IRole[]>([])
    const [permissions, setPermissions] = useState<IPermission[]>([])
    const [loading, setLoading] = useState(true)
    const [showCreateUserModal, setShowCreateUserModal] = useState(false)
    const [showEditUserModal, setShowEditUserModal] = useState(false)
    const [editingUserId, setEditingUserId] = useState<number | null>(null)
    const [editingUsername, setEditingUsername] = useState('')
    const [editingUserNumber, setEditingUserNumber] = useState('')
    const [editingPassword, setEditingPassword] = useState('')
    const [selectedUserId, setSelectedUserId] = useState<number | null>(null)
    const [newUsername, setNewUsername] = useState('')
    const [newPassword, setNewPassword] = useState('')
    const [selectedUserRoleIds, setSelectedUserRoleIds] = useState<number[]>([])
    const [selectedUserOverrides, setSelectedUserOverrides] = useState<IUserPermissionOverride[]>([])
    const [selectedUserDevices, setSelectedUserDevices] = useState<IUserMobileDevice[]>([])
    const [loadingUserDevices, setLoadingUserDevices] = useState(false)
    const [editingDecisionDeviceIds, setEditingDecisionDeviceIds] = useState<string[]>([])
    const [permSearch, setPermSearch] = useState('')
    const [collapsedPermGroups, setCollapsedPermGroups] = useState<Set<string>>(new Set())

    useEffect(() => {
        appRef.setHeader(<HiUsers className="me-2" size={28} />, () => (isHebrew ? 'ניהול משתמשים והרשאות' : 'Users & Permissions'))
    }, [isHebrew])

    const load = async () => {
        setLoading(true)
        try {
            const [usersData, rolesData, permissionsData] = await Promise.all([
                fetchUsers(),
                listRoles(),
                getPermissionCatalog(),
            ])
            setUsers(usersData)
            setRoles(rolesData.data)
            setPermissions(permissionsData.data)
            if (usersData.length > 0 && selectedUserId === null) setSelectedUserId(usersData[0].id)
        } catch (error: any) {
            toast.error(error?.message || 'Failed to load users management')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        void load()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (selectedUserId == null) return
            ; (async () => {
                try {
                    const [roleIds, overrides] = await Promise.all([getUserRoles(selectedUserId), getUserOverrides(selectedUserId)])
                    setSelectedUserRoleIds(roleIds.data)
                    setSelectedUserOverrides(overrides.data)
                } catch (error: any) {
                    toast.error(error?.message || 'Failed to load user permissions')
                }
            })()
    }, [selectedUserId])

    useEffect(() => {
        if (selectedUserId == null) return
            ; (async () => {
                try {
                    setLoadingUserDevices(true)
                    const rows = await listUserMobileDevices(selectedUserId)
                    setSelectedUserDevices(rows.data)
                } catch (error: any) {
                    toast.error(error?.message || (isHebrew ? 'טעינת מכשירי מובייל נכשלה' : 'Failed to load mobile devices'))
                } finally {
                    setLoadingUserDevices(false)
                }
            })()
    }, [isHebrew, selectedUserId])

    const selectedUser = useMemo(() => users.find(user => user.id === selectedUserId) ?? null, [users, selectedUserId])

    // ── Grouped permission logic (matching roles-permissions pattern) ──

    const permOverridesByKey = useMemo(
        () => new Map(selectedUserOverrides.map(o => [o.permissionKey, o])),
        [selectedUserOverrides],
    )

    const groupedPermissions = useMemo(() => {
        const searchLower = permSearch.toLowerCase().trim()
        const grouped = new Map<string, IPermission[]>()
        for (const perm of permissions) {
            let groupKey: string
            if (perm.key.startsWith('settings.ltsp101')) groupKey = 'settings.ltsp101'
            else if (perm.key.startsWith('settings.fragmented')) groupKey = 'settings.fragmented'
            else if (perm.key.startsWith('settings')) groupKey = 'settings'
            else if (perm.key.startsWith('module.')) groupKey = 'module'
            else groupKey = perm.key.split('.')[0]
            if (!grouped.has(groupKey)) grouped.set(groupKey, [])
            grouped.get(groupKey)!.push(perm)
        }

        const groupOrder = new Map(PERMISSION_GROUP_ORDER.map((k, i) => [k, i]))
        const sortedGroups = Array.from(grouped.keys()).sort((a, b) => {
            const ai = groupOrder.get(a) ?? 999
            const bi = groupOrder.get(b) ?? 999
            if (ai !== bi) return ai - bi
            return a.localeCompare(b)
        })

        const result: { groupKey: string; items: IPermission[] }[] = []
        for (const groupKey of sortedGroups) {
            const items = grouped.get(groupKey)!
            if (!searchLower) {
                result.push({ groupKey, items })
                continue
            }
            const filtered = items.filter(perm => {
                const label = getPermissionLabel(perm.key, isHebrew ? 'he' : 'en').toLowerCase()
                return perm.key.toLowerCase().includes(searchLower) || label.includes(searchLower)
            })
            if (filtered.length > 0) result.push({ groupKey, items: filtered })
        }
        return result
    }, [permissions, permSearch, isHebrew])

    const togglePermGroup = (group: string) => {
        setCollapsedPermGroups(prev => {
            const next = new Set(prev)
            if (next.has(group)) next.delete(group)
            else next.add(group)
            return next
        })
    }

    const countOverridesInGroup = (keys: string[]) =>
        keys.filter(k => permOverridesByKey.has(k)).length

    const onCreateUser = async () => {
        if (!newUsername.trim() || !newPassword.trim()) return
        try {
            await createUser(newUsername.trim(), newPassword.trim())
            setNewUsername('')
            setNewPassword('')
            setShowCreateUserModal(false)
            toast.success(isHebrew ? 'המשתמש נוצר' : 'User created')
            await load()
        } catch (error: any) {
            toast.error(error?.message || (isHebrew ? 'יצירת משתמש נכשלה' : 'Failed to create user'))
        }
    }

    const openEditUserModal = (user: IUser) => {
        setEditingUserId(user.id)
        setEditingUsername(user.username || '')
        setEditingUserNumber(`${user.userNumber ?? ''}`)
        setEditingPassword('')
        setShowEditUserModal(true)
    }

    const onSaveEditedUser = async () => {
        if (editingUserId == null) return
        const username = editingUsername.trim()
        const numberText = editingUserNumber.trim()
        if (!username) {
            toast.error(isHebrew ? 'שם משתמש לא יכול להיות ריק' : 'Username cannot be empty')
            return
        }
        if (!/^[0-9]+$/.test(numberText)) {
            toast.error(isHebrew ? 'מספר משתמש לא חוקי' : 'Invalid user number')
            return
        }
        const userNumber = Number.parseInt(numberText, 10)
        const patch: Partial<IUser> = { username, userNumber }
        if (editingPassword.trim().length > 0) {
            patch.password = editingPassword.trim()
        }
        try {
            await patchUser(editingUserId, patch)
            await load()
            setShowEditUserModal(false)
            setEditingUserId(null)
            setEditingPassword('')
            toast.success(isHebrew ? 'המשתמש עודכן' : 'User updated')
        } catch (error: any) {
            toast.error(error?.message || (isHebrew ? 'עדכון משתמש נכשל' : 'Failed to update user'))
        }
    }

    const toggleUserRole = (roleId: number) => {
        setSelectedUserRoleIds(prev => (prev.includes(roleId) ? prev.filter(item => item !== roleId) : [...prev, roleId]))
    }

    const toggleUserOverride = (permissionKey: string, allowed: boolean) => {
        setSelectedUserOverrides(prev => {
            const without = prev.filter(item => item.permissionKey !== permissionKey)
            return [...without, { permissionKey, allowed }]
        })
    }

    const clearUserOverride = (permissionKey: string) => {
        setSelectedUserOverrides(prev => prev.filter(item => item.permissionKey !== permissionKey))
    }

    const selectAllOverride = (keys: string[]) => {
        for (const key of keys) toggleUserOverride(key, true)
    }
    const clearAllOverride = (keys: string[]) => {
        for (const key of keys) clearUserOverride(key)
    }

    const saveUserPermissions = async () => {
        if (selectedUserId == null) return
        try {
            await replaceUserRoles(selectedUserId, selectedUserRoleIds)
            await replaceUserOverrides(selectedUserId, selectedUserOverrides)
            toast.success(isHebrew ? 'הרשאות נשמרו' : 'Permissions saved')
            await load()
        } catch (error: any) {
            toast.error(error?.message || (isHebrew ? 'שמירה נכשלה' : 'Save failed'))
        }
    }

    if (loading) {
        return <div className="text-gray-600 dark:text-gray-300">{isHebrew ? 'טוען...' : 'Loading...'}</div>
    }

    return (
        <div className="space-y-6">
            <section className="rounded-xl bg-white dark:bg-gray-800 shadow p-5">
                <div className="mb-3 flex items-center justify-between gap-3">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{isHebrew ? 'משתמשים' : 'Users'}</h2>
                    <button
                        className="rounded bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2"
                        onClick={() => setShowCreateUserModal(true)}
                    >
                        {isHebrew ? 'צור משתמש' : 'Create User'}
                    </button>
                </div>
                <div className="overflow-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-black">
                    <table className="min-w-full text-sm text-gray-900 dark:text-gray-100">
                        <thead>
                            <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-100">
                                <th className="py-2 px-2 text-start font-semibold">{isHebrew ? 'מזהה' : 'ID'}</th>
                                <th className="py-2 px-2 text-start font-semibold">{isHebrew ? 'שם משתמש' : 'Username'}</th>
                                <th className="py-2 px-2 text-start font-semibold">{isHebrew ? 'מספר משתמש' : 'User Number'}</th>
                                <th className="py-2 px-2 text-start font-semibold">{isHebrew ? 'סטטוס' : 'Status'}</th>
                                <th className="py-2 px-2 text-start font-semibold">{isHebrew ? 'תפקידים' : 'Roles'}</th>
                                <th className="py-2 px-2 text-start font-semibold">{isHebrew ? 'פעולות' : 'Actions'}</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-black">
                            {users.map(user => (
                                <tr
                                    key={user.id}
                                    className={`border-t border-gray-200 dark:border-gray-700 ${selectedUserId === user.id
                                            ? 'bg-blue-100 dark:bg-blue-900 border-blue-300 dark:border-blue-700'
                                            : 'bg-white hover:bg-gray-50 dark:bg-black dark:hover:bg-gray-900'
                                        }`}
                                >
                                    <td className="py-2 px-2 text-gray-900 dark:text-gray-100">{user.id}</td>
                                    <td className="py-2 px-2 text-gray-900 dark:text-gray-100">{user.username}</td>
                                    <td className="py-2 px-2 text-gray-900 dark:text-gray-100">{user.userNumber ?? '-'}</td>
                                    <td className="py-2 px-2 text-gray-900 dark:text-gray-100">{user.isActive === false ? (isHebrew ? 'מושבת' : 'Disabled') : isHebrew ? 'פעיל' : 'Active'}</td>
                                    <td className="py-2 px-2 text-gray-900 dark:text-gray-100">{(user.roleNames || []).join(', ') || '-'}</td>
                                    <td className="py-2 px-2 flex gap-2">
                                        <button className={`rounded border px-2 py-1 ${selectedUserId === user.id
                                                ? 'border-blue-600 bg-blue-600 text-white hover:bg-blue-700 dark:border-blue-500 dark:bg-blue-600 dark:hover:bg-blue-700'
                                                : 'border-gray-300 bg-white text-gray-900 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600'
                                            }`} onClick={() => setSelectedUserId(user.id)}>
                                            {isHebrew ? 'ערוך הרשאות' : 'Edit Permissions'}
                                        </button>
                                        <button
                                            className="rounded border border-gray-300 bg-white px-2 py-1 text-gray-900 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
                                            onClick={() => openEditUserModal(user)}
                                        >
                                            {isHebrew ? 'ערוך משתמש' : 'Edit User'}
                                        </button>
                                        <button
                                            className="rounded border border-gray-300 bg-white px-2 py-1 text-gray-900 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
                                            onClick={async () => {
                                                try {
                                                    await patchUser(user.id, { isActive: !(user.isActive !== false) })
                                                    await load()
                                                } catch (error: any) {
                                                    toast.error(error?.message || 'Update failed')
                                                }
                                            }}
                                        >
                                            {user.isActive === false ? (isHebrew ? 'הפעל' : 'Enable') : isHebrew ? 'השבת' : 'Disable'}
                                        </button>
                                        <button
                                            className="rounded bg-red-600 text-white px-2 py-1"
                                            onClick={async () => {
                                                if (!window.confirm(isHebrew ? 'למחוק משתמש?' : 'Delete user?')) return
                                                try {
                                                    await removeUser(user.id)
                                                    await load()
                                                } catch (error: any) {
                                                    toast.error(error?.message || 'Delete failed')
                                                }
                                            }}
                                        >
                                            {isHebrew ? 'מחק' : 'Delete'}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {selectedUser && (
                <section className="rounded-xl bg-white dark:bg-gray-800 shadow p-5 space-y-4">
                    <div>
                        <div className="font-semibold mb-2 text-gray-900 dark:text-gray-100">{isHebrew ? 'אישורי כניסה לאפליקציה' : 'App Login Approvals'}</div>
                        <div className="max-h-80 overflow-auto border border-gray-300 dark:border-gray-700 rounded p-2 bg-white dark:bg-gray-900">
                            {loadingUserDevices && <div className="text-sm text-gray-600 dark:text-gray-300">{isHebrew ? 'טוען...' : 'Loading...'}</div>}
                            {!loadingUserDevices && selectedUserDevices.length === 0 && (
                                <div className="text-sm text-gray-600 dark:text-gray-300">{isHebrew ? 'אין מכשירים רשומים' : 'No registered devices yet'}</div>
                            )}
                            {!loadingUserDevices &&
                                selectedUserDevices.map(device => {
                                    const isApproved = !!device.approvedAt && !device.revokedAt
                                    const isDenied = !!device.revokedAt
                                    const isPending = !isApproved && !isDenied
                                    const isEditingDecision = editingDecisionDeviceIds.includes(device.id)
                                    return (
                                        <div key={device.id} className="border border-gray-200 dark:border-gray-700 rounded p-2 mb-2 last:mb-0">
                                            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                                {device.deviceName || (isHebrew ? 'מכשיר לא מזוהה' : 'Unknown device')} ({device.devicePlatform || '-'})
                                            </div>
                                            <div className="text-xs text-gray-600 dark:text-gray-300 break-all">{device.fingerprint}</div>
                                            <div className="text-xs text-gray-600 dark:text-gray-300">{device.ipAddress || '-'}</div>
                                            <div className="text-xs text-gray-600 dark:text-gray-300">
                                                {isHebrew ? 'נראה לאחרונה: ' : 'Last seen: '}
                                                {new Date(device.lastSeenAt).toLocaleString()}
                                            </div>
                                            <div className="mt-2 flex gap-2">
                                                <span
                                                    className={`rounded px-2 py-1 text-xs ${isApproved ? 'bg-green-700 text-white' : isDenied ? 'bg-red-700 text-white' : 'bg-amber-700 text-white'
                                                        }`}
                                                >
                                                    {isApproved
                                                        ? isHebrew
                                                            ? 'מאושר'
                                                            : 'Approved'
                                                        : isDenied
                                                            ? isHebrew
                                                                ? 'נדחה'
                                                                : 'Denied'
                                                            : isHebrew
                                                                ? 'ממתין לאישור'
                                                                : 'Pending Approval'}
                                                </span>
                                                {(isPending || isEditingDecision) && (
                                                    <>
                                                        <button
                                                            className="rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
                                                            onClick={async () => {
                                                                if (selectedUserId == null) return
                                                                try {
                                                                    await setUserMobileDeviceApproval(selectedUserId, device.id, true)
                                                                    const rows = await listUserMobileDevices(selectedUserId)
                                                                    setSelectedUserDevices(rows.data)
                                                                    setEditingDecisionDeviceIds(prev => prev.filter(id => id !== device.id))
                                                                    toast.success(
                                                                        isHebrew
                                                                            ? 'המכשיר אושר'
                                                                            : 'Device approved',
                                                                    )
                                                                } catch (error: any) {
                                                                    toast.error(error?.message || (isHebrew ? 'עדכון מכשיר נכשל' : 'Failed to update device'))
                                                                }
                                                            }}
                                                        >
                                                            {isHebrew ? 'אשר' : 'Approve'}
                                                        </button>
                                                        <button
                                                            className="rounded border border-red-400 bg-red-600 px-2 py-1 text-xs text-white hover:bg-red-700"
                                                            onClick={async () => {
                                                                if (selectedUserId == null) return
                                                                try {
                                                                    await setUserMobileDeviceApproval(selectedUserId, device.id, false)
                                                                    const rows = await listUserMobileDevices(selectedUserId)
                                                                    setSelectedUserDevices(rows.data)
                                                                    setEditingDecisionDeviceIds(prev => prev.filter(id => id !== device.id))
                                                                    toast.success(isHebrew ? 'המכשיר נדחה/נחסם' : 'Device denied/revoked')
                                                                } catch (error: any) {
                                                                    toast.error(error?.message || (isHebrew ? 'עדכון מכשיר נכשל' : 'Failed to update device'))
                                                                }
                                                            }}
                                                        >
                                                            {isHebrew ? 'דחה' : 'Deny'}
                                                        </button>
                                                        <button
                                                            className="rounded border border-red-500 bg-transparent px-2 py-1 text-xs text-red-600 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-900/30"
                                                            onClick={async () => {
                                                                if (selectedUserId == null) return
                                                                if (!window.confirm(isHebrew ? 'למחוק את המכשיר הזה?' : 'Delete this device?')) return
                                                                try {
                                                                    await deleteUserMobileDevice(selectedUserId, device.id)
                                                                    const rows = await listUserMobileDevices(selectedUserId)
                                                                    setSelectedUserDevices(rows.data)
                                                                    setEditingDecisionDeviceIds(prev => prev.filter(id => id !== device.id))
                                                                    toast.success(isHebrew ? 'המכשיר נמחק' : 'Device deleted')
                                                                } catch (error: any) {
                                                                    toast.error(error?.message || (isHebrew ? 'מחיקת מכשיר נכשלה' : 'Failed to delete device'))
                                                                }
                                                            }}
                                                        >
                                                            {isHebrew ? 'מחק מכשיר' : 'Delete Device'}
                                                        </button>
                                                        {isEditingDecision && (
                                                            <button
                                                                className="rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
                                                                onClick={() =>
                                                                    setEditingDecisionDeviceIds(prev => prev.filter(id => id !== device.id))
                                                                }
                                                            >
                                                                {isHebrew ? 'בטל' : 'Cancel'}
                                                            </button>
                                                        )}
                                                    </>
                                                )}
                                                {!isPending && !isEditingDecision && (
                                                    <>
                                                        <button
                                                            className="rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
                                                            onClick={() =>
                                                                setEditingDecisionDeviceIds(prev =>
                                                                    prev.includes(device.id) ? prev : [...prev, device.id],
                                                                )
                                                            }
                                                        >
                                                            {isHebrew ? 'ערוך החלטה' : 'Edit decision'}
                                                        </button>
                                                        <button
                                                            className="rounded border border-red-500 bg-transparent px-2 py-1 text-xs text-red-600 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-900/30"
                                                            onClick={async () => {
                                                                if (selectedUserId == null) return
                                                                if (!window.confirm(isHebrew ? 'למחוק את המכשיר הזה?' : 'Delete this device?')) return
                                                                try {
                                                                    await deleteUserMobileDevice(selectedUserId, device.id)
                                                                    const rows = await listUserMobileDevices(selectedUserId)
                                                                    setSelectedUserDevices(rows.data)
                                                                    toast.success(isHebrew ? 'המכשיר נמחק' : 'Device deleted')
                                                                } catch (error: any) {
                                                                    toast.error(error?.message || (isHebrew ? 'מחיקת מכשיר נכשלה' : 'Failed to delete device'))
                                                                }
                                                            }}
                                                        >
                                                            {isHebrew ? 'מחק מכשיר' : 'Delete Device'}
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )
                                })}
                        </div>
                    </div>
                    <div>
                        <div className="font-semibold mb-2 text-gray-900 dark:text-gray-100">{isHebrew ? 'תפקידים' : 'Roles'}</div>
                        <div className="grid md:grid-cols-3 gap-2">
                            {roles.map(role => (
                                <label key={role.id} className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
                                    <input type="checkbox" checked={selectedUserRoleIds.includes(role.id)} onChange={() => toggleUserRole(role.id)} />
                                    <span>{role.name}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                    <div>
                        <div className="font-semibold mb-2 text-gray-900 dark:text-gray-100">
                            {isHebrew ? 'חריגות הרשאה (Allow/Deny)' : 'Permission Overrides (Allow/Deny)'}
                        </div>

                        {/* Search */}
                        <div className="flex items-center rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 mb-2">
                            <svg className="ms-3 text-gray-400 shrink-0" stroke="currentColor" fill="none" viewBox="0 0 24 24" width="18" height="18">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <input
                                className="w-full border-none bg-transparent px-2 py-2 text-gray-900 dark:text-gray-100 outline-none"
                                placeholder={isHebrew ? 'חיפוש הרשאות...' : 'Search permissions...'}
                                value={permSearch}
                                onChange={e => setPermSearch(e.target.value)}
                            />
                        </div>

                        {/* Grouped list */}
                        <div className="max-h-[70vh] overflow-y-auto space-y-2 border border-gray-300 dark:border-gray-700 rounded p-2 bg-white dark:bg-gray-900">
                            {groupedPermissions.length === 0 && (
                                <div className="text-gray-500 text-sm py-4 text-center">
                                    {isHebrew ? 'לא נמצאו הרשאות התואמות לחיפוש' : 'No permissions match your search'}
                                </div>
                            )}
                            {groupedPermissions.map(({ groupKey, items }) => {
                                const groupLabel =
                                    PERMISSION_GROUP_LABELS[groupKey]?.[isHebrew ? 'he' : 'en'] ?? groupKey
                                const isCollapsed = collapsedPermGroups.has(groupKey)
                                const overriddenCount = countOverridesInGroup(items.map(p => p.key))
                                const allOverridden = overriddenCount === items.length
                                const groupAction = allOverridden
                                    ? () => clearAllOverride(items.map(p => p.key))
                                    : () => selectAllOverride(items.map(p => p.key))

                                return (
                                    <div key={groupKey} className="border border-gray-200 dark:border-gray-700 rounded overflow-hidden">
                                        <div
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => togglePermGroup(groupKey)}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault()
                                                    togglePermGroup(groupKey)
                                                }
                                            }}
                                            className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-800 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 text-sm select-none"
                                        >
                                            <div className="flex items-center gap-2">
                                                {isCollapsed
                                                    ? <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                                    : <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                                }
                                                <span className="font-medium text-gray-800 dark:text-gray-100">{groupLabel}</span>
                                                <span className="text-xs text-gray-500">({overriddenCount}/{items.length})</span>
                                            </div>
                                            <button
                                                onClick={e => { e.stopPropagation(); groupAction() }}
                                                className="text-xs text-indigo-600 hover:underline"
                                            >
                                                {allOverridden
                                                    ? (isHebrew ? 'נקה הכל' : 'Clear all')
                                                    : (isHebrew ? 'סמן הכל' : 'Select all')}
                                            </button>
                                        </div>
                                        {!isCollapsed && (
                                            <div className="divide-y divide-gray-100 dark:divide-gray-800">
                                                {items.map(perm => {
                                                    const override = permOverridesByKey.get(perm.key)
                                                    const label = getPermissionLabel(perm.key, isHebrew ? 'he' : 'en')
                                                    return (
                                                        <div key={perm.id} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-700">
                                                            <div className="flex-1 flex flex-col min-w-0">
                                                                <span className="text-gray-800 dark:text-gray-200 truncate">{label}</span>
                                                                <code className="text-xs text-gray-400 dark:text-gray-500 truncate">{perm.key}</code>
                                                            </div>
                                                            <button
                                                                className={`rounded px-2 py-1 text-xs ${override?.allowed === true ? 'bg-green-600 text-white' : 'border border-gray-300 bg-white text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100'}`}
                                                                onClick={() => toggleUserOverride(perm.key, true)}
                                                            >
                                                                {isHebrew ? 'אפשר' : 'Allow'}
                                                            </button>
                                                            <button
                                                                className={`rounded px-2 py-1 text-xs ${override?.allowed === false ? 'bg-red-600 text-white' : 'border border-gray-300 bg-white text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100'}`}
                                                                onClick={() => toggleUserOverride(perm.key, false)}
                                                            >
                                                                {isHebrew ? 'מנע' : 'Deny'}
                                                            </button>
                                                            <button
                                                                className="rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
                                                                onClick={() => clearUserOverride(perm.key)}
                                                            >
                                                                {isHebrew ? 'נקה' : 'Clear'}
                                                            </button>
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                    <div>
                        <button className="rounded bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2" onClick={saveUserPermissions}>
                            {isHebrew ? 'שמור הרשאות משתמש' : 'Save User Permissions'}
                        </button>
                    </div>
                </section>
            )}

            {showCreateUserModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4">
                    <div className="w-full max-w-lg rounded-xl bg-white dark:bg-gray-800 shadow-xl p-5">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                            {isHebrew ? 'יצירת משתמש חדש' : 'Create User'}
                        </h3>
                        <div className="space-y-3">
                            <input
                                className="w-full rounded border border-gray-300 px-3 py-2 text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                                placeholder={isHebrew ? 'שם משתמש' : 'Username'}
                                value={newUsername}
                                onChange={event => setNewUsername(event.target.value)}
                            />
                            <input
                                className="w-full rounded border border-gray-300 px-3 py-2 text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                                placeholder={isHebrew ? 'סיסמה' : 'Password'}
                                type="password"
                                value={newPassword}
                                onChange={event => setNewPassword(event.target.value)}
                            />
                        </div>
                        <div className="mt-4 flex items-center gap-2 justify-end">
                            <button
                                className="rounded border border-gray-300 bg-white px-3 py-2 text-gray-900 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
                                onClick={() => {
                                    setShowCreateUserModal(false)
                                    setNewUsername('')
                                    setNewPassword('')
                                }}
                            >
                                {isHebrew ? 'בטל' : 'Cancel'}
                            </button>
                            <button className="rounded bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2" onClick={onCreateUser}>
                                {isHebrew ? 'צור משתמש' : 'Create User'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showEditUserModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4">
                    <div className="w-full max-w-lg rounded-xl bg-white dark:bg-gray-800 shadow-xl p-5">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                            {isHebrew ? 'עריכת משתמש' : 'Edit User'}
                        </h3>
                        <div className="space-y-3">
                            <input
                                className="w-full rounded border border-gray-300 px-3 py-2 text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                                placeholder={isHebrew ? 'שם משתמש' : 'Username'}
                                value={editingUsername}
                                onChange={event => setEditingUsername(event.target.value)}
                            />
                            <input
                                className="w-full rounded border border-gray-300 px-3 py-2 text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                                placeholder={isHebrew ? 'מספר משתמש' : 'User Number'}
                                value={editingUserNumber}
                                onChange={event => setEditingUserNumber(event.target.value)}
                            />
                            <input
                                className="w-full rounded border border-gray-300 px-3 py-2 text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                                placeholder={isHebrew ? 'סיסמה חדשה (אופציונלי)' : 'New Password (optional)'}
                                type="password"
                                value={editingPassword}
                                onChange={event => setEditingPassword(event.target.value)}
                            />
                        </div>
                        <div className="mt-4 flex items-center gap-2 justify-end">
                            <button
                                className="rounded border border-gray-300 bg-white px-3 py-2 text-gray-900 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
                                onClick={() => {
                                    setShowEditUserModal(false)
                                    setEditingUserId(null)
                                    setEditingPassword('')
                                }}
                            >
                                {isHebrew ? 'בטל' : 'Cancel'}
                            </button>
                            <button className="rounded bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2" onClick={onSaveEditedUser}>
                                {isHebrew ? 'שמור' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    )
}
